import { ItemsPerPage } from '@billy/design-system'
import { useEffect, useMemo, useRef } from 'react'
import { useQuery } from 'react-query'

import { fetchImportedInvoices } from '@features/importedInvoices/query-api'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { defaultStaleTime } from '../../../config/queryClient'
import { PeriodMode } from '../../../enums/periodMode'
import { QueryKeys } from '../../../enums/queryKeys'
import { SortDirection } from '../../../enums/sortDirection'
import { usePeriodDates } from '../../../hooks/usePeriodDates'
import { Paging } from '../../../types/metable'
import { ImportedInvoicesSortProperty } from '../enums/importedInvoicesSortProperty'
import { getImportedInvoicesTableData } from '../utils/getImportedInvoicesTableData'

interface UseImportedInvoicesListProps {
  isEnabled?: boolean
  page?: number
  pageSize?: ItemsPerPage
  period?: string
  searchQuery?: string
  sortDirection?: SortDirection
  sortProperty?: ImportedInvoicesSortProperty
}

export const useImportedInvoicesList = ({
  isEnabled = true,
  page,
  pageSize,
  period,
  searchQuery,
  sortDirection,
  sortProperty,
}: UseImportedInvoicesListProps = {}) => {
  const previousPagination = useRef<Paging | undefined>(undefined)
  // apiZervant only gets the `organizations/:id` prefix once the organization has loaded.
  const { organization } = useUserOrganization()
  const organizationId = organization?.id
  const { isLoading: isLoadingPeriodDates, fromDate, toDate } = usePeriodDates(period)
  const isWaitingForPeriodDates = isLoadingPeriodDates && Boolean(period) && period !== PeriodMode.All

  const queryParams = useMemo(
    () => ({
      ...(fromDate && toDate ? { fromDate, toDate } : {}),
      ...(page ? { page } : {}),
      ...(pageSize ? { pageSize } : {}),
      ...(searchQuery ? { searchQuery } : {}),
      ...(sortDirection ? { sortDirection } : {}),
      ...(sortProperty ? { sortProperty } : {}),
    }),
    [fromDate, page, pageSize, searchQuery, sortDirection, sortProperty, toDate],
  )

  const {
    data,
    isFetched,
    isFetching,
    isLoading: isLoadingQuery,
    isSuccess,
  } = useQuery([QueryKeys.ImportedInvoices, organizationId, queryParams], () => fetchImportedInvoices(queryParams), {
    enabled: !!organizationId && !isWaitingForPeriodDates && isEnabled,
    keepPreviousData: true,
    staleTime: defaultStaleTime,
  })

  useEffect(() => {
    if (data?.data?.meta.paging) {
      previousPagination.current = { ...data.data.meta.paging }
    }
  }, [data?.data?.meta.paging])

  const documents = useMemo(() => data?.data?.documents ?? [], [data?.data?.documents])
  const invoices = useMemo(() => getImportedInvoicesTableData(documents), [documents])

  const isLoading = isLoadingQuery || !organizationId || isWaitingForPeriodDates

  return {
    invoices,
    isFetched,
    isFetching,
    isLoading,
    isSuccess,
    pagination: data?.data?.meta.paging ?? previousPagination.current,
  }
}
